import {HTMLReactParserOptions, Element as ParserElement} from 'html-react-parser';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ClientDynamicContent, Icon, NodeElement} from 'components';
import {AFTER_HOURS_DEFAULT_TEMPLATE, DynamicComponentNames, AfterHoursUnits as Units} from 'constants/client';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {dateUtils, dynamicContentUtils} from 'utils';
import {formatDate} from 'utils/transform';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.AFTER_HOURS;

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {description, title, nextWorkingHoursLabel, nextWorkingHoursDate, localParticipant} = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{title}</NodeElement>;
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.Icon, node)) {
                return (
                    <NodeElement node={node}>
                        <Icon name='agent-after-hours' size='8xl' />
                    </NodeElement>);
            }

            if (nextWorkingHoursDate) {
                if (isMatchElement(Units.NextWorkingHoursLabel, node)) {
                    return <NodeElement node={node}>{nextWorkingHoursLabel}</NodeElement>;
                }
                if (isMatchElement(Units.NextWorkingHoursDate, node)) {
                    return <NodeElement node={node}>{nextWorkingHoursDate}</NodeElement>;
                }
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const AfterHoursView: React.FC = () => {
    const {t} = useTranslation();
    const {image, timeZone, dateFormat, timeFormat, workingHours} = useClientSession();
    const dateTimeFormat = useMemo(() => dateUtils.getDateTimeFormat(dateFormat, timeFormat), [dateFormat, timeFormat]);

    const title = useMemo(() => t(T_KEYS.TITLE), [t]);
    const description = useMemo(() => t(T_KEYS.DESCRIPTION), [t]);
    const nextWorkingHoursLabel = useMemo(() => t(T_KEYS.NEXT_WORKING_HOURS), [t]);
    const nextWorkingHoursDate = useMemo(() =>
        formatDate(workingHours.nextAvailableDate, timeZone, dateTimeFormat), [timeZone, dateTimeFormat, workingHours]);

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.AfterHours}
                defaultTemplate={AFTER_HOURS_DEFAULT_TEMPLATE}
                optionsProps={{
                    description, title, nextWorkingHoursLabel, nextWorkingHoursDate
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};